/**
 * The zone this viewer reads the app in, and the override that pins it
 * (#11055).
 *
 * The browser is the default answer because it follows the person: someone who
 * flies to another timezone sees their new local days without doing anything.
 * It is not always the *right* answer, though, and the two cases that break it
 * are both real -- a person who travels but keeps working to home time, and a
 * person who works to a client's calendar rather than their own. Neither had
 * any way to say so, so this module adds one.
 *
 * The override lives here and only here, client-side. There is deliberately no
 * `users.timezone` column: the backend stays organization-only, and the
 * frontend owns the viewer's zone. What the backend receives is whatever this
 * module resolves, on every request, which is why *every* sender has to read
 * it -- a setting that takes effect on the screens that render dates but not
 * on the ones that filter, export or prompt is worse than no setting at all.
 *
 * Resolution order, once and for the whole app:
 *
 *   pinned override -> browser -> organization -> `America/New_York`
 */

import { readCachedCurrentUserId } from "@/hooks/useCurrentUser";
import {
  SELECTABLE_TIME_ZONES,
  browserTimeZone,
  isValidTimeZone,
  viewerTimeZone,
} from "./temporal";

// The zones a viewer may pin are the zones the app offers anywhere, so the list
// lives with the rest of the temporal module rather than here: the viewer
// picker, the organization picker and the occurrence editor cannot be allowed
// to disagree about what counts as a valid zone. Re-exported because this
// module is where the *preference* half of the question lives.
export { SELECTABLE_TIME_ZONES };

const SELECTABLE_TIME_ZONE_SET = new Set(SELECTABLE_TIME_ZONES);

/**
 * Whether a zone is one this app will render in.
 *
 * Stricter than `isValidTimeZone`, deliberately. `Intl` accepts a bare offset
 * (`+08:00`) as a time zone, and an offset has no daylight saving rule, so it
 * is exactly the kind of value a *stored* preference must not be allowed to
 * be -- it would be correct for half the year.
 */
export const isSelectableTimeZone = (zone: string): boolean =>
  SELECTABLE_TIME_ZONE_SET.size > 0
    ? SELECTABLE_TIME_ZONE_SET.has(zone)
    : // No tz database to check against (an engine without
      // `supportedValuesOf`). Fall back to the loose check plus the two rules
      // the list would otherwise have enforced.
      isValidTimeZone(zone) &&
      !zone.startsWith("Etc/") &&
      !/^[+-]\d/.test(zone);

/**
 * What a viewer has said about their own zone.
 *
 * A discriminated value rather than "a string, empty meaning the browser":
 * following the browser is a state a person actively chooses and has to be
 * able to get back to, not the absence of one. The `kind` tag is the same
 * device `granularity` is on the wire -- it says which arm the value occupies,
 * so adding a third answer later (the organization's zone, say) is a compile
 * error at every site that has to care rather than a silent fallthrough.
 */
export type ViewerTimeZonePreference =
  | { readonly kind: "browser" }
  | { readonly kind: "fixed"; readonly zone: string };

/** Track wherever this browser says it is. The default, and the way back. */
export const FOLLOW_BROWSER: ViewerTimeZonePreference = { kind: "browser" };

const STORAGE_PREFIX = "fp_viewer_timezone_v1";

/**
 * Where one viewer's preference is kept.
 *
 * Scoped per user id, because a shared browser profile is common and a
 * colleague inheriting your pinned zone would be indistinguishable from the
 * bug this feature exists to fix.
 */
export const viewerTimeZoneStorageKey = (viewerKey: string): string =>
  `${STORAGE_PREFIX}:${viewerKey}`;

const subscribers = new Set<() => void>();

/**
 * Re-render every reader when the preference changes.
 *
 * The zone is read in hundreds of places per page, so pinning one has to take
 * effect immediately rather than on the next navigation.
 */
export const subscribeToViewerTimeZone = (
  onChange: () => void
): (() => void) => {
  subscribers.add(onChange);
  return () => {
    subscribers.delete(onChange);
  };
};

/**
 * Read a viewer's stored preference, validating it on the way out.
 *
 * Every failure resolves to following the browser rather than throwing. A
 * stored zone can genuinely go stale -- IANA renames zones, and a value
 * written by an older build outlives the name -- and this runs behind every
 * rendered row, so a throw here would take a page down over a preference.
 *
 * A `null` key means nobody is signed in yet, which is the browser's zone by
 * definition: there is no "whose preference" to answer.
 */
export const readViewerTimeZonePreference = (
  viewerKey: string | null
): ViewerTimeZonePreference => {
  if (!viewerKey) return FOLLOW_BROWSER;
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(viewerTimeZoneStorageKey(viewerKey));
  } catch {
    // localStorage throws in private browsing and over quota.
    return FOLLOW_BROWSER;
  }
  if (!raw) return FOLLOW_BROWSER;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return FOLLOW_BROWSER;
  }
  if (typeof parsed !== "object" || parsed === null) return FOLLOW_BROWSER;

  const candidate = parsed as Record<string, unknown>;
  if (candidate.kind !== "fixed") return FOLLOW_BROWSER;
  const zone = candidate.zone;
  if (typeof zone !== "string" || !isSelectableTimeZone(zone))
    return FOLLOW_BROWSER;
  return { kind: "fixed", zone };
};

/** Store a viewer's preference and tell every reader about it. */
export const writeViewerTimeZonePreference = (
  viewerKey: string | null,
  preference: ViewerTimeZonePreference
): void => {
  if (viewerKey) {
    try {
      localStorage.setItem(
        viewerTimeZoneStorageKey(viewerKey),
        JSON.stringify(preference)
      );
    } catch {
      // Nothing to do: the preference simply won't survive the session.
    }
  }
  subscribers.forEach((onChange) => onChange());
};

/**
 * The zone a preference resolves to.
 *
 * The exhaustive switch is the point: a third kind of preference added later
 * fails to compile here instead of quietly falling through to "follow the
 * browser".
 */
export const resolveViewerTimeZone = (
  preference: ViewerTimeZonePreference,
  browser: string | null,
  organizationTimeZone?: string | null
): string => {
  switch (preference.kind) {
    case "fixed":
      // Already validated on read, so this needs no second opinion.
      return preference.zone;
    case "browser":
      return viewerTimeZone(organizationTimeZone, browser);
    default: {
      const unhandled: never = preference;
      throw new Error(
        `unhandled viewer timezone preference: ${JSON.stringify(unhandled)}`
      );
    }
  }
};

let memoizedBrowserZone: string | null | undefined;

/**
 * The zone the browser reports, resolved once per session.
 *
 * Memoized because this is read once per rendered file-tree row, and a browser
 * does not change the zone it reports while a page is open -- a laptop that
 * lands somewhere else picks the new zone up on the next load, and a viewer
 * who wants it sooner now has an override to say so. Computed lazily rather
 * than at module load so that importing this module has no side effect and a
 * test can install its own `Intl` before the first read.
 */
export const cachedBrowserTimeZone = (): string | null => {
  if (memoizedBrowserZone === undefined)
    memoizedBrowserZone = browserTimeZone();
  return memoizedBrowserZone;
};

/** Whose preference to read, outside a React tree. */
export const currentViewerKey = (): string | null => readCachedCurrentUserId();

/**
 * The pinned zone, or `null` when this viewer follows their browser.
 *
 * A plain string (or null) so it can be a `useSyncExternalStore` snapshot
 * without allocating a fresh object on every render.
 */
export const currentViewerTimeZoneOverride = (): string | null => {
  const preference = readViewerTimeZonePreference(currentViewerKey());
  return preference.kind === "fixed" ? preference.zone : null;
};

/**
 * The effective viewer zone, for callers that are not React components.
 *
 * This is what every request-building sender reads. It resolves the override
 * and the browser but not the organization's zone, which only exists behind a
 * fetch -- a request body cannot wait on one. That arm is not lost: the
 * backend resolves the organization's zone itself whenever the viewer zone it
 * receives is absent or unusable, and `useViewerTimeZone` folds it in for
 * anything being rendered.
 */
export const currentViewerTimeZone = (): string =>
  resolveViewerTimeZone(
    readViewerTimeZonePreference(currentViewerKey()),
    cachedBrowserTimeZone()
  );
